// Invoice-related filters

import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
	createRadioFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const invoiceFilters: FilterRegistry = {
	invoiceKeywords: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search by invoice ID, external ID, or client name...',
			objectTable: 'invoice',
			listKeys: ['invoice', 'invoice-client-embedded'],
			fulltextFields: [
				'externalId',
				'externalId2',
				'_id',
				'c.name',
				'c.externalId',
				'c.externalId2',
			],
		}),
		queryParamName: 'invoiceKeywords',
	},

	invoiceCompany: {
		...createEntityLookupFilter({
			label: 'Company',
			placeholder: 'Search companies...',
			apiEndpoint: '/users/companies',
			objectTable: 'invoice',
			fieldName: 'fk_company__id',
			listKeys: ['invoice', 'invoice-client-embedded'],
			displayFields: {
				id: 'id',
				primary: 'label',
			},
			colorScheme: 'purple',
		}),
		queryParamName: 'invoiceCompany',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	invoiceDateRange: {
		...createDateRangeFilter({
			label: 'Invoice Date',
			objectTable: 'invoice',
			dateField: 'date',
			listKeys: ['invoice', 'invoice-client-embedded'],
		}),
		queryParamName: 'invoiceDateRange',
	},

	invoiceDueDateRange: {
		...createDateRangeFilter({
			label: 'Due Date',
			objectTable: 'invoice',
			dateField: 'dueDate',
			listKeys: ['invoice', 'invoice-client-embedded'],
		}),
		queryParamName: 'invoiceDueDateRange',
		queryMapping: {
			objectTable: 'invoice',
			dateField: 'dueDate',
			joinType: 'direct',
			// When filtering "dueDate >= today" (Current tab), also include invoices
			// with no due date — they have no missed deadline so they're not past due
			includeNullDates: true,
		},
	},

	invoiceSalesRep: {
		type: 'multi-select',
		label: 'Sales Rep',
		placeholder: 'Select sales representatives...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/invoices/salesrep/options',
		options: [{ id: 'ME', label: 'Me' }],
		queryParamName: 'invoiceSalesRep',
		multiple: true,
		queryMapping: {
			objectTable: 'invoice',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		listKeys: ['invoice', 'invoice-client-embedded'],
	},

	invoicePaymentStatus: {
		...createRadioFilter({
			label: 'Payment Status',
			options: [
				{ id: 'all', label: 'All' },
				{ id: 'outstanding', label: 'Outstanding' },
				{ id: 'paid_in_full', label: 'Paid in Full' },
			],
			defaultValue: { id: 'all', label: 'All' },
			objectTable: 'invoice',
			fieldName: 'paymentStatus',
			listKeys: ['invoice', 'invoice-client-embedded'],
		}),
		queryParamName: 'invoicePaymentStatus',
		queryMapping: {
			objectTable: 'invoice',
			joinType: 'custom',
		},
	},
};
